import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import {
    FontFamilies,
    Colors,
    FontSizes,
    FontWeights,
    LineHeights,
    MediaQueries
} from "@paclife/plds.tokens";

const FontBase = css`
    font-family: ${FontFamilies.openSans}, sans-serif;
    text-align: ${({ align }) => align};
    display: ${({ display }) => display};
    font-weight: ${({ bold }) => bold && FontWeights[700]};
    margin: 0;
    word-wrap: break-word;
    vertical-align: middle;
`;

const H1 = styled.h1`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-size: ${FontSizes[1500]}; // 28.22px
    line-height: ${LineHeights[500]};
    font-weight: ${FontWeights[700]};
    @media (max-width: ${MediaQueries.lg}) {
        font-size: ${FontSizes[1300]}; //25.01px
    }
`;

const H2 = styled.h2`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-size: ${FontSizes[1200]}; // 22.58px
    line-height: ${LineHeights[500]};
    font-weight: ${FontWeights[700]};
    @media (max-width: ${MediaQueries.lg}) {
        font-size: ${FontSizes[1000]}; //20px
    }
`;

const H3 = styled.h3`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-size: ${FontSizes[900]}; // 18.06px
    line-height: ${LineHeights[300]};
    font-weight: ${FontWeights[700]};
`;

// same as Title, Subtitle
const Body = styled.p`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-size: ${FontSizes[700]}; // 16px
    line-height: ${LineHeights[200]};
`;

const Caption = styled.span`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral700)};
    font-size: ${FontSizes[600]}; // 14.45px
    line-height: ${LineHeights[200]};
`;

const Disclosure = styled.span`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral700)};
    font-size: ${FontSizes[600]}; // 14.45px
    line-height: ${LineHeights[200]};
`;

const MicroCopy = styled.span`
    ${FontBase};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-size: ${FontSizes[400]}; // 12.8px
    line-height: ${LineHeights.root};
`;

export const ButtonLinkFont = css`
    font-family: ${FontFamilies.openSans};
    font-size: ${FontSizes[700]};
    color: ${({ dark }) => (dark ? Colors.neutral0 : Colors.neutral1000)};
    font-weight: ${FontWeights[700]};
    line-height: ${LineHeights[200]};
`;

const VariantMapping = {
    h1: H1,
    h2: H2,
    h3: H3,
    body: Body,
    title: Body,
    subtitle: Body,
    microcopy: MicroCopy,
    disclosure: Disclosure,
    caption: Caption
};

const Typography = ({
    className,
    children,
    variant,
    dark,
    bold,
    align,
    display
}) => {
    const Component = VariantMapping[variant];

    return (
        <Component
            className={className}
            dark={dark}
            bold={bold}
            align={align}
            display={display}
        >
            {children}
        </Component>
    );
};

export default Typography;

Typography.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    variant: PropTypes.oneOf([
        "h1",
        "h2",
        "h3",
        // 'h4',
        "body",
        "title",
        "subtitle",
        "microcopy",
        "disclosure",
        "caption"
    ]),
    dark: PropTypes.bool,
    bold: PropTypes.bool,
    align: PropTypes.oneOf(["inherit", "left", "center", "right", "justify"]),
    display: PropTypes.oneOf(["initial", "block", "inline"])
};

Typography.defaultProps = {
    className: "",
    variant: "body",
    dark: false,
    bold: false,
    align: "inherit",
    display: "initial"
};
